import {
    Alert,
    Badge,
    Button,
    Checkbox,
    Group,
    Loader,
    Paper,
    Stack,
    Table,
    TagsInput,
    Text,
    TextInput,
    Title,
} from '@mantine/core'
import {useForm} from '@mantine/form'
import {useState} from 'react'

import {userAccountStatusColor} from 'src/theme/semantic-colors'

import {useAllServiceTypes, useCreateServiceType, useUpdateServiceType} from './use-service-types'

type CreateServiceTypeFormValues = {
    name: string
}

type RenameServiceTypeFormValues = {
    name: string
}

export function ServiceTypesSection() {
    const {data: serviceTypes, isLoading, isError, error, refetch} = useAllServiceTypes()
    const createServiceType = useCreateServiceType()
    const updateServiceType = useUpdateServiceType()
    const [editingId, setEditingId] = useState<string | null>(null)

    const createForm = useForm<CreateServiceTypeFormValues>({
        initialValues: {name: ''},
        validate: (values) => ({
            name: values.name.trim() === '' ? 'Name is required' : null,
        }),
    })

    const renameForm = useForm<RenameServiceTypeFormValues>({
        initialValues: {name: ''},
        validate: (values) => ({
            name: values.name.trim() === '' ? 'Name is required' : null,
        }),
    })

    async function handleCreate() {
        const validation = createForm.validate()
        if (validation.hasErrors) {
            return
        }
        try {
            await createServiceType.mutateAsync({name: createForm.values.name})
        } catch {
            return
        }
        createForm.reset()
    }

    function startRename(id: string, currentName: string) {
        setEditingId(id)
        renameForm.setValues({name: currentName})
    }

    async function handleSaveRename(id: string) {
        const validation = renameForm.validate()
        if (validation.hasErrors) {
            return
        }
        try {
            await updateServiceType.mutateAsync({id, input: {name: renameForm.values.name}})
        } catch (caught) {
            renameForm.setFieldError(
                'name',
                caught instanceof Error ? caught.message : 'Failed to rename category',
            )
            return
        }
        setEditingId(null)
    }

    async function handleToggleActive(id: string, currentActive: boolean) {
        await updateServiceType.mutateAsync({id, input: {active: !currentActive}})
    }

    function handleSpecificTypesChange(id: string, specificTypes: string[]) {
        updateServiceType.mutate({id, input: {specificTypes}})
    }

    if (isLoading) {
        return (
            <Group justify="center" py="md">
                <Loader />
            </Group>
        )
    }

    if (isError) {
        return (
            <Stack gap="sm">
                <Alert color="red" title="Could not load work categories">
                    {error instanceof Error ? error.message : 'Something went wrong.'}
                </Alert>
                <Button onClick={() => void refetch()} variant="light">
                    Retry
                </Button>
            </Stack>
        )
    }

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="md">
                <Stack gap={4}>
                    <Title order={3}>Work categories</Title>
                    <Text size="sm" c="dimmed">
                        Manage the work categories and specific types available when creating jobs.
                        Inactive categories remain visible on existing jobs but cannot be selected
                        for new jobs.
                    </Text>
                </Stack>

                {createServiceType.isError && (
                    <Alert color="red" title="Could not create category">
                        {createServiceType.error instanceof Error
                            ? createServiceType.error.message
                            : 'Something went wrong.'}
                    </Alert>
                )}

                <Group align="flex-end" gap="sm">
                    <TextInput
                        label="New category"
                        placeholder="Enter category name"
                        style={{flex: 1}}
                        value={createForm.values.name}
                        onChange={(event) =>
                            createForm.setFieldValue('name', event.currentTarget.value)
                        }
                        error={createForm.errors.name}
                    />
                    <Button
                        onClick={() => void handleCreate()}
                        loading={createServiceType.isPending}
                        disabled={createForm.values.name.trim() === ''}
                    >
                        Add
                    </Button>
                </Group>

                {serviceTypes && serviceTypes.length > 0 ? (
                    <Table striped highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Name</Table.Th>
                                <Table.Th>Specific types</Table.Th>
                                <Table.Th>Technician work</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th>Actions</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {serviceTypes.map((serviceType) => (
                                <Table.Tr key={serviceType.id}>
                                    <Table.Td>
                                        {editingId === serviceType.id ? (
                                            <TextInput
                                                size="xs"
                                                value={renameForm.values.name}
                                                onChange={(event) =>
                                                    renameForm.setFieldValue(
                                                        'name',
                                                        event.currentTarget.value,
                                                    )
                                                }
                                                error={renameForm.errors.name}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                        void handleSaveRename(serviceType.id)
                                                    }
                                                }}
                                            />
                                        ) : (
                                            serviceType.name
                                        )}
                                    </Table.Td>
                                    <Table.Td miw={240}>
                                        <TagsInput
                                            size="xs"
                                            aria-label={`Specific types for ${serviceType.name}`}
                                            placeholder="Type and press Enter"
                                            value={serviceType.specificTypes}
                                            onChange={(specificTypes) =>
                                                handleSpecificTypesChange(
                                                    serviceType.id,
                                                    specificTypes,
                                                )
                                            }
                                        />
                                    </Table.Td>
                                    <Table.Td>
                                        <Checkbox
                                            aria-label={`Technician work: ${serviceType.name}`}
                                            checked={serviceType.isTechnicianWork}
                                            onChange={(event) =>
                                                updateServiceType.mutate({
                                                    id: serviceType.id,
                                                    input: {
                                                        isTechnicianWork:
                                                            event.currentTarget.checked,
                                                    },
                                                })
                                            }
                                        />
                                    </Table.Td>
                                    <Table.Td>
                                        {serviceType.active ? (
                                            <Badge
                                                color={userAccountStatusColor(true)}
                                                variant="light"
                                            >
                                                Active
                                            </Badge>
                                        ) : (
                                            <Badge
                                                color={userAccountStatusColor(false)}
                                                variant="light"
                                            >
                                                Inactive
                                            </Badge>
                                        )}
                                    </Table.Td>
                                    <Table.Td>
                                        <Group gap="xs">
                                            {editingId === serviceType.id ? (
                                                <>
                                                    <Button
                                                        size="xs"
                                                        variant="light"
                                                        onClick={() =>
                                                            void handleSaveRename(serviceType.id)
                                                        }
                                                        loading={updateServiceType.isPending}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="xs"
                                                        variant="subtle"
                                                        onClick={() => setEditingId(null)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        size="xs"
                                                        variant="light"
                                                        onClick={() =>
                                                            startRename(
                                                                serviceType.id,
                                                                serviceType.name,
                                                            )
                                                        }
                                                    >
                                                        Rename
                                                    </Button>
                                                    <Button
                                                        size="xs"
                                                        variant="subtle"
                                                        color={serviceType.active ? 'red' : 'green'}
                                                        onClick={() =>
                                                            void handleToggleActive(
                                                                serviceType.id,
                                                                serviceType.active,
                                                            )
                                                        }
                                                        loading={updateServiceType.isPending}
                                                    >
                                                        {serviceType.active
                                                            ? 'Deactivate'
                                                            : 'Activate'}
                                                    </Button>
                                                </>
                                            )}
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                ) : (
                    <Text size="sm" c="dimmed">
                        No categories yet. Add one above.
                    </Text>
                )}
            </Stack>
        </Paper>
    )
}
